import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { profileAPI } from '../../services/api';
import {
  AVATAR_ACCEPTED_TYPES,
  AVATAR_MAX_SOURCE_BYTES,
  resizeImageToDataUrl,
} from '../../services/avatarImage';
import { AppButton } from '../ui/AppButton';
import { SectionCard } from '../ui/SectionCard';
import { Field, TextInput } from './fields';

const TEXT_FIELDS = ['full_name', 'organization', 'phone'];

const initialsOf = (user) =>
  (user?.full_name || user?.email || 'U')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const profileFrom = (user) => ({
  full_name: user?.full_name || '',
  organization: user?.organization || '',
  phone: user?.phone || '',
  avatar_url: user?.avatar_url || null,
});

/**
 * The Hub-owned part of the profile: name, company, phone and picture. Email
 * is the SSO sign-in address and is read-only here. Only changed fields are
 * sent, and the signed-in user is refreshed so the header and sidebar update.
 */
export const ProfileCard = () => {
  const { user, updateUser } = useAuth();
  const saved = useMemo(() => profileFrom(user), [user]);
  const [form, setForm] = useState(saved);
  const [saving, setSaving] = useState(false);
  const [readingImage, setReadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setForm(saved);
  }, [saved]);

  const changes = useMemo(() => {
    const result = {};
    TEXT_FIELDS.forEach((field) => {
      const value = form[field].trim();
      if (value !== saved[field].trim()) result[field] = value;
    });
    if (form.avatar_url !== saved.avatar_url) result.avatar_url = form.avatar_url;
    return result;
  }, [form, saved]);
  const dirty = Object.keys(changes).length > 0;

  const setField = (field) => (event) => {
    const { value } = event.target;
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!AVATAR_ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Choose a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > AVATAR_MAX_SOURCE_BYTES) {
      toast.error('Choose an image of 10 MB or less.');
      return;
    }
    setReadingImage(true);
    try {
      const avatar = await resizeImageToDataUrl(file);
      setForm((previous) => ({ ...previous, avatar_url: avatar }));
    } catch {
      toast.error('That image could not be read. Try another file.');
    } finally {
      setReadingImage(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!dirty) return;
    setSaving(true);
    try {
      const result = await profileAPI.updateProfile(changes);
      updateUser(profileFrom(result));
      toast.success('Profile saved');
    } catch (error) {
      toast.error(error?.message || 'Your profile could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Profile" subtitle="How you appear to other people in the Hub.">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        <div className="flex items-center gap-4">
          {form.avatar_url ? (
            <img
              src={form.avatar_url}
              alt={form.full_name || user?.email || 'You'}
              className="w-16 h-16 rounded-full object-cover border-2 border-black/[0.06] flex-shrink-0"
            />
          ) : (
            <span
              className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-black/[0.06] text-white text-[20px] font-bold select-none"
              style={{ background: 'linear-gradient(135deg, #3D6B38, #6BAF65)' }}
              aria-hidden="true"
            >
              {initialsOf(user)}
            </span>
          )}
          <div className="flex flex-col items-start gap-1.5 min-w-0">
            <div className="flex flex-wrap gap-2">
              <AppButton
                variant="secondary"
                size="sm"
                loading={readingImage}
                onClick={() => fileInputRef.current?.click()}
              >
                Change picture
              </AppButton>
              {form.avatar_url && (
                <AppButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setForm((previous) => ({ ...previous, avatar_url: null }))}
                >
                  Remove picture
                </AppButton>
              )}
            </div>
            <p className="m-0 text-[12px] leading-[1.45] text-lb-muted">
              PNG, JPEG or WebP. It is cropped to a square and resized to 256 px.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={AVATAR_ACCEPTED_TYPES.join(',')}
              className="hidden"
              onChange={handleImage}
              data-testid="avatar-file"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field id="settings-full-name" label="Full name">
            <TextInput
              id="settings-full-name"
              value={form.full_name}
              onChange={setField('full_name')}
              maxLength={200}
              autoComplete="name"
            />
          </Field>
          <Field id="settings-phone" label="Phone">
            <TextInput
              id="settings-phone"
              type="tel"
              value={form.phone}
              onChange={setField('phone')}
              maxLength={50}
              autoComplete="tel"
            />
          </Field>
          <Field id="settings-organization" label="Company or institution" className="sm:col-span-2">
            <TextInput
              id="settings-organization"
              value={form.organization}
              onChange={setField('organization')}
              maxLength={200}
              autoComplete="organization"
            />
          </Field>
          <Field
            id="settings-email"
            label="Email"
            hint="This is your sign-in address. Change it in your SSO account."
            className="sm:col-span-2"
          >
            <TextInput
              id="settings-email"
              value={user?.email || ''}
              readOnly
              aria-describedby="settings-email-hint"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2">
          {dirty && (
            <AppButton variant="ghost" onClick={() => setForm(saved)} disabled={saving}>
              Discard changes
            </AppButton>
          )}
          <AppButton type="submit" variant="primary" loading={saving} disabled={!dirty || readingImage}>
            Save changes
          </AppButton>
        </div>
      </form>
    </SectionCard>
  );
};
